import * as Sentry from "@sentry/react";

// Anti-bot protection for the better-auth login/signup pages. AWS WAF runs a silent browser check
// in front of the auth endpoints; we fetch its token and send it on the request so real users pass
// without seeing anything, while bots that can't run the check are blocked. The URL comes from Rails
// (window.awsWafIntegrationUrl) and is empty in dev and tests, where this does nothing.

const WAF_TOKEN_HEADER = "x-aws-waf-token";

interface AwsWafIntegration {
  // Runs the check if needed and returns the token to send to WAF.
  getToken: () => Promise<string>;
}

declare global {
  interface Window {
    // Set once AWS's script has loaded.
    AwsWafIntegration?: AwsWafIntegration;
  }
}

// Set while the script is downloading, so concurrent calls share one load. Cleared once it settles
// (a failed load can retry). Whether the SDK is already loaded is tracked by window.AwsWafIntegration
// itself, so there's no long-lived flag of our own.
let sdkLoading: Promise<void> | null = null;

// Load AWS's script once. It sets window.AwsWafIntegration.
function loadSdk(integrationUrl: string): Promise<void> {
  if (window.AwsWafIntegration) {
    return Promise.resolve();
  }
  if (sdkLoading) {
    return sdkLoading;
  }
  sdkLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    // integrationUrl is set from AWS_WAF_INTEGRATION_URL and expected without a trailing slash.
    script.src = `${integrationUrl}/challenge.js`;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load AWS WAF integration SDK"));
    document.head.appendChild(script);
  }).finally(() => {
    sdkLoading = null;
  });
  return sdkLoading;
}

/**
 * Fetches a WAF token and returns it as a header for the auth request, e.g.
 * `authClient.signIn.email(body, { headers: await awsWafTokenHeaders() })`.
 *
 * Returns {} when WAF isn't set up, or if loading/solving fails — we let the request through rather
 * than block a real user (WAF still has the final say at the edge).
 */
export async function awsWafTokenHeaders(): Promise<Record<string, string>> {
  const integrationUrl = window.awsWafIntegrationUrl;
  if (!integrationUrl) {
    return {};
  }
  try {
    await loadSdk(integrationUrl);
    const token = await window.AwsWafIntegration?.getToken();
    return token ? { [WAF_TOKEN_HEADER]: token } : {};
  } catch (error) {
    Sentry.captureException(error);
    return {};
  }
}
